import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  normalizePrefixValueHelper,
  sanitizePhoneNumber
} from '../../../../../helpers/formatPhoneHelper'
import { getCustomerDomain } from '../../../../../redux'
import { CardActionProps } from '../../../cardActions/cardActions'
import SimpleDialog from '../../../simpleDialog/simpleDialog'

interface IncidentReplyFormDialogProps {
  open: boolean
  onClose: (formData?: any) => void
  incidentReplyList: SelectOption[]
  isGasNatural: boolean
}

interface IncidentReplyFormModel {
  incidentReplyType?: string
  newContact?: string
  newPrefix?: string
  newNumber?: string
  newEmail?: string
  comments?: string
}

const initialFormData: IncidentReplyFormModel = {
  incidentReplyType: undefined,
  newContact: undefined,
  newPrefix: '+34',
  newNumber: undefined,
  newEmail: undefined,
  comments: undefined
}

export const IncidentReplyFormDialog: React.FC<IncidentReplyFormDialogProps> = ({
  open,
  onClose,
  incidentReplyList,
  isGasNatural
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'procedureTable'])
  const [formData, setFormData] = useState<IncidentReplyFormModel>(initialFormData)
  const [formValid, setFormValid] = useState(false)
  const [isNewContactSelected, setIsNewContactSelected] = useState(false)
  const [isValidEmail, setIsValidEmail] = useState(true)
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true)
  const [isValidPrefix, setIsValidPrefix] = useState(true)

  useEffect(() => {
    if (open) {
      setFormData(initialFormData)
      setIsNewContactSelected(false)
      setIsValidEmail(true)
      setIsValidPhoneNumber(true)
      setIsValidPrefix(true)
      setFormValid(false)
    }
  }, [open])

  useEffect(() => {
    let valid = !!formData.incidentReplyType
    if (isNewContactSelected) {
      valid =
        valid &&
        !!formData.newContact &&
        !!formData.newPrefix &&
        !!formData.newNumber &&
        // !!formData.newEmail &&
        isValidEmail &&
        isValidPhoneNumber &&
        isValidPrefix
    }
    setFormValid(valid)
  }, [formData, isNewContactSelected, isValidEmail, isValidPhoneNumber, isValidPrefix])

  const sections: Section[] = [
    {
      inputs: [
        {
          name: 'incidentReplyType',
          label: t('procedureTable:components.forms.inputs.incidentReplyType'),
          type: DataType.SELECT,
          selectOptions: incidentReplyList,
          required: true,
          inputSize: 6,
          onChange: value => {
            setFormData(prev => ({
              ...prev,
              ...(value !== '01' ? initialFormData : {}),
              incidentReplyType: value.toString()
            }))
            setIsNewContactSelected(value === '01')
          }
        },
        {
          name: 'newContact',
          label: t('procedureTable:components.forms.inputs.newContact'),
          type: DataType.TEXT,
          required: isNewContactSelected,
          display: isNewContactSelected,
          onChange: value => {
            setFormData(prev => ({ ...prev, newContact: value.toString() }))
          }
        },
        {
          name: 'newPrefix',
          label: t('procedureTable:components.forms.inputs.newPrefix'),
          type: DataType.TEXT,
          required: isNewContactSelected,
          display: isNewContactSelected,
          defaultValue: '+34',
          regexp: '^(\\+[1-9][0-9]{0,2}|00[1-9][0-9]{0,2})$',
          errorText: t('procedureTable:components.forms.errorText.newPrefix'),
          helpText: t('procedureTable:components.forms.helpText.newPrefix'),
          maxLength: 5,
          errorState: !isValidPrefix,
          onChange: value => {
            const newPrefixValue = value.toString()
            setIsValidPrefix(
              new RegExp('^(\\+[1-9][0-9]{0,2}|00[1-9][0-9]{0,2})$').test(newPrefixValue)
            )
            setFormData(prev => ({
              ...prev,
              newPrefix: normalizePrefixValueHelper(newPrefixValue)
            }))
          }
        },
        {
          name: 'newNumber',
          label: t('procedureTable:components.forms.inputs.newNumber'),
          type: DataType.TEXT,
          required: isNewContactSelected,
          maxLength: 15,
          regexp: '^$|^(\\d[\\s\\-]?){6,14}\\d$',
          helpText: t('procedureTable:components.forms.helpText.newNumber'),
          errorText: t('procedureTable:components.forms.errorText.newNumber'),
          display: isNewContactSelected,
          errorState: !isValidPhoneNumber,
          onChange: value => {
            const newNumberValue = value.toString()
            setIsValidPhoneNumber(new RegExp('^$|^(\\d[\\s\\-]?){8,14}\\d$').test(newNumberValue))
            setFormData(prev => ({
              ...prev,
              newNumber: sanitizePhoneNumber(newNumberValue)
            }))
          }
        },
        {
          name: 'newEmail',
          label: t('procedureTable:components.forms.inputs.newEmail'),
          type: DataType.TEXT,
          regexp: '^$|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,}$',
          errorText: t('procedureTable:components.forms.errorText.newEmail'),
          display: isNewContactSelected,
          errorState: !isValidEmail,
          onChange: value => {
            const newEmailValue = value.toString()
            setIsValidEmail(
              new RegExp('^$|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,}$').test(newEmailValue)
            )
            setFormData(prev => ({
              ...prev,
              newEmail: newEmailValue
            }))
          }
        },
        {
          name: 'comments',
          label: t('procedureTable:components.forms.inputs.comments'),
          type: DataType.LONGTEXT,
          maxLength: 400,
          display: isGasNatural,
          onChange: value => {
            setFormData(prev => ({
              ...prev,
              comments: value.toString()
            }))
          }
        }
      ]
    }
  ]

  const options: FormOptions = {
    elevation: 0,
    columns: 2,
    analytics: {
      customer: getCustomerDomain(),
      view: t('procedureTable:components.forms.analytics.incidentReplyFormDialog')
    }
  }

  // Footer actions for SimpleDialog
  const footerActions: CardActionProps[] = [
    {
      label: t('globalerisLibrary:form.actions.cancel'),
      color: 'secondary',
      handleActionClick: () => onClose(undefined)
    },
    {
      label: t('globalerisLibrary:form.actions.save'),
      color: 'primary',
      disabled: !formValid,
      handleActionClick: () => onClose(formData)
    }
  ]

  return (
    <SimpleDialog
      open={open}
      handleClose={() => onClose(undefined)}
      footerActions={footerActions}
      maxWidth="md"
      formStyle="formContent"
    >
      <Form
        sections={sections}
        data={formData}
        options={options}
        title={t('procedureTable:components.incidentReplyFormDialog.title')}
      />
    </SimpleDialog>
  )
}
